// The boat's owners, on Boat settings (TOM-458, ADR 0319). Every owner is
// equal: anyone listed here can invite an address, revoke an open invite, or
// remove another owner. Leaving the boat yourself is in its own panel below,
// where Delete is for a boat with one owner.
//
// The list shows each owner's name and address. That is the one place a
// co-owner's identity is shown; board history still says "a co-owner".

import { useState, type FormEvent } from 'react';

import {
  inviteOwner,
  ownerLabel,
  ownersErrorMessage,
  removeOwner,
  revokeInvite,
  type OwnersState,
} from '../../lib/boatOwners';

interface BoatOwnersPanelProps {
  boatId: string;
  boatName: string;
  state: OwnersState;
  refresh: () => void;
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function BoatOwnersPanel({ boatId, boatName, state, refresh }: BoatOwnersPanelProps) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [armedRemove, setArmedRemove] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function onInvite(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const address = email.trim();
    if (address.length === 0) return;
    setSending(true);
    setError(null);
    setSentTo(null);
    try {
      const { invite } = await inviteOwner(boatId, address);
      setSentTo(invite.email);
      setEmail('');
      refresh();
    } catch (err) {
      setError(ownersErrorMessage(err, 'Could not send the invite. Try again.'));
    } finally {
      setSending(false);
    }
  }

  async function onRevoke(inviteId: string) {
    setBusyId(inviteId);
    setError(null);
    try {
      await revokeInvite(boatId, inviteId);
    } catch (err) {
      setError(ownersErrorMessage(err, 'Could not withdraw the invite. Try again.'));
    } finally {
      setBusyId(null);
      refresh();
    }
  }

  async function onRemove(userId: string) {
    setBusyId(userId);
    setError(null);
    try {
      await removeOwner(boatId, userId);
      setArmedRemove(null);
    } catch (err) {
      setError(ownersErrorMessage(err, 'Could not remove that owner. Try again.'));
    } finally {
      setBusyId(null);
      refresh();
    }
  }

  return (
    <div className="panel" data-testid="boat-owners">
      <h3>Owners</h3>
      <p className="muted">
        Every owner of {boatName} sees its live data, history, alarms and boards, gets its alarms on
        their phone, and can invite or remove owners. Each owner&apos;s chats with Neomaris, notes
        and notification settings stay their own.
      </p>

      {state.kind === 'loading' ? <p className="muted">Loading owners…</p> : null}
      {state.kind === 'error' ? (
        <p className="alarm-form-err" role="alert">
          Could not load the owners.{' '}
          <button type="button" className="btn" onClick={refresh}>
            Try again
          </button>
        </p>
      ) : null}

      {state.kind === 'ready' ? (
        <ul className="tb-list owners-list" data-testid="boat-owners-list">
          {state.owners.map((o) => (
            <li key={o.userId} className="tb-list__row" data-testid="boat-owner">
              <span className="tb-list__body">
                <span className="owners-list__text">
                  <strong className="owners-list__name">
                    {o.displayName?.trim() || o.email}
                    {o.you ? <span className="owners-list__you"> You</span> : null}
                  </strong>
                  <span className="owners-list__meta">
                    {o.displayName?.trim() ? `${o.email} · ` : ''}Owner since {day(o.grantedAt)}
                  </span>
                </span>
                {o.you ? null : armedRemove === o.userId ? (
                  <span className="owners-list__actions">
                    <button
                      type="button"
                      className="btn owners-remove"
                      disabled={busyId === o.userId}
                      data-testid="boat-owner-remove-confirm"
                      onClick={() => void onRemove(o.userId)}
                    >
                      {busyId === o.userId ? 'Removing…' : 'Remove'}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      disabled={busyId === o.userId}
                      onClick={() => setArmedRemove(null)}
                    >
                      Keep
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    className="btn owners-remove"
                    data-testid="boat-owner-remove"
                    aria-label={`Remove ${ownerLabel(o)}`}
                    onClick={() => setArmedRemove(o.userId)}
                  >
                    Remove
                  </button>
                )}
              </span>
            </li>
          ))}
          {state.invites.map((i) => (
            <li key={i.id} className="tb-list__row" data-testid="boat-invite">
              <span className="tb-list__body">
                <span className="owners-list__text">
                  <strong className="owners-list__name">{i.email}</strong>
                  <span className="owners-list__meta">
                    Invited{i.invitedBy?.displayName ? ` by ${i.invitedBy.displayName}` : ''} · link
                    works until {day(i.expiresAt)}
                  </span>
                </span>
                <button
                  type="button"
                  className="btn"
                  disabled={busyId === i.id}
                  data-testid="boat-invite-revoke"
                  aria-label={`Withdraw the invite to ${i.email}`}
                  onClick={() => void onRevoke(i.id)}
                >
                  {busyId === i.id ? 'Withdrawing…' : 'Withdraw'}
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {armedRemove !== null && state.kind === 'ready' ? (
        <p className="muted" data-testid="boat-owner-remove-warning">
          {(() => {
            const o = state.owners.find((x) => x.userId === armedRemove);
            return o === undefined
              ? null
              : `${ownerLabel(o)} will stop seeing ${boatName} and getting its alarms. Neomaris emails them and the other owners.`;
          })()}
        </p>
      ) : null}

      <form className="owners-invite" onSubmit={(e) => void onInvite(e)}>
        <label htmlFor="owner-invite-email">Invite an owner</label>
        <div className="owners-invite__row">
          <input
            id="owner-invite-email"
            className="form-input"
            type="email"
            autoComplete="off"
            placeholder="name@example.com"
            value={email}
            maxLength={320}
            data-testid="owner-invite-email"
            onChange={(e) => {
              setEmail(e.target.value);
              setSentTo(null);
            }}
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={sending || email.trim().length === 0}
            data-testid="owner-invite-send"
          >
            {sending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
        <p className="muted owners-invite__help">
          Neomaris emails a link that works for 14 days. They accept it signed in with that address.
        </p>
      </form>
      {sentTo !== null ? (
        <p className="profile-confirm" role="status" data-testid="owner-invite-sent">
          Invite sent to {sentTo}.
        </p>
      ) : null}
      {error !== null ? (
        <p className="alarm-form-err" role="alert" data-testid="boat-owners-error">
          {error}
        </p>
      ) : null}
    </div>
  );
}
